import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { EMPTY, Subject, fromEvent, interval } from 'rxjs';
import { catchError, filter, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface AppVersion {
  version: string;
}

/**
 * Service to check for application version updates.
 * Polls the backend every 5 minutes and when the tab becomes visible
 * to detect when a new version is deployed.
 *
 * If an update is available and the user doesn't reload within 5 minutes,
 * the app will automatically reload.
 */
@Injectable({
  providedIn: 'root',
})
export class AppVersionService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/api/v1/app/version`;
  private readonly destroy$ = new Subject<void>();
  private readonly currentVersion = this.getCurrentVersion();
  private updateAvailableSince: number | null = null;

  readonly isUpdateAvailable = signal<boolean>(false);

  constructor() {
    // Skip version checking in local development
    if (this.currentVersion === 'dev' || !this.currentVersion) {
      return;
    }

    // Poll every 5 minutes
    interval(5 * 60 * 1000)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.checkVersion()),
        catchError(() => EMPTY)
      )
      .subscribe();

    // Check when tab becomes visible (using Visibility API)
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(
          takeUntil(this.destroy$),
          filter(() => !document.hidden),
          tap(() => this.checkVersion()),
          catchError(() => EMPTY)
        )
        .subscribe();
    }

    // Check immediately on startup
    this.checkVersion();

    // Monitor for auto-reload after 5 minutes
    interval(60 * 1000) // Check every minute
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkAutoReload());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCurrentVersion(): string {
    if (typeof document === 'undefined') {
      return '';
    }
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag?.getAttribute('content') || '';
  }

  private checkVersion(): void {
    this.http.get<AppVersion>(this.apiUrl)
      .pipe(catchError(() => EMPTY))
      .subscribe((response) => {
        if (response?.version) {
          // Only show update if backend version is semantically newer
          // This prevents showing updates during deployment window when
          // frontend and backend versions might temporarily mismatch
          if (this.isNewerVersion(response.version, this.currentVersion)) {
            this.isUpdateAvailable.set(true);
            // Record when update became available (if not already set)
            if (this.updateAvailableSince === null) {
              this.updateAvailableSince = Date.now();
            }
          } else if (response.version !== this.currentVersion) {
            // Versions differ but backend is not newer - likely deployment in progress
            // Don't show update yet, but also don't clear it if already shown
            // This handles the case where user reloaded during deployment window
            // and now has new frontend but old backend
            if (!this.isUpdateAvailable()) {
              // Only clear if we haven't already shown an update
              // This prevents flickering during deployment
            }
          } else {
            // Versions match, clear update state
            this.isUpdateAvailable.set(false);
            this.updateAvailableSince = null;
          }
        }
      });
  }

  /**
   * Compare two version strings to determine if the first is newer than the second.
   * Handles semantic versions (e.g., "v1.4.0", "1.4.0", "v1.4.1") and dev timestamp versions.
   */
  private isNewerVersion(version1: string, version2: string): boolean {
    // Skip comparison for dev versions or empty
    if (version2 === 'dev' || version2 === '' || !version2) {
      return false;
    }

    // Normalize versions by removing 'v' prefix
    const normalizeVersion = (v: string): number[] => {
      const cleaned = v.replace(/^v/i, '');

      // Handle dev-* timestamp versions (e.g., "dev-20260109203000")
      if (cleaned.startsWith('dev-')) {
        const timestamp = cleaned.replace('dev-', '');
        return [0, 0, parseInt(timestamp, 10) || 0];
      }

      // Split semantic version into parts (major.minor.patch)
      const parts = cleaned.split('.').map(part => {
        const numericPart = part.replace(/[^0-9].*$/, '');
        return parseInt(numericPart, 10) || 0;
      });

      // Ensure we have at least 3 parts
      while (parts.length < 3) {
        parts.push(0);
      }

      return parts.slice(0, 3);
    };

    const v1Parts = normalizeVersion(version1);
    const v2Parts = normalizeVersion(version2);

    // Compare major, minor, patch in order
    for (let i = 0; i < 3; i++) {
      if (v1Parts[i] > v2Parts[i]) {
        return true;
      }
      if (v1Parts[i] < v2Parts[i]) {
        return false;
      }
    }

    // Versions are equal
    return false;
  }

  private checkAutoReload(): void {
    // If update has been available for 5 minutes, force reload
    if (this.isUpdateAvailable() && this.updateAvailableSince !== null) {
      const minutesSinceUpdate = (Date.now() - this.updateAvailableSince) / (60 * 1000);
      if (minutesSinceUpdate >= 5) {
        this.reloadApp();
      }
    }
  }

  reloadApp(): void {
    window.location.reload();
  }

  /**
   * Get the current version loaded in the browser.
   */
  getCurrentVersionString(): string {
    return this.currentVersion || 'dev';
  }
}
